import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { X, Package } from 'lucide-react';
import { inventoryAPI } from '@shared/apiClient';
import toast from 'react-hot-toast';

export default function InventoryFormModal({ item, isOpen, onClose, onCreated, mode = 'create' }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    itemNumber: '',
    description: '',
    purchaseDescription: '',
    isActive: true,
    quantityOnHand: 0,
    cost: 0,
    price: 0,
    reorderPoint: '',
    preferredVendor: '',
    manufacturerPartNumber: '',
    salesTaxCode: '',
    account: '',
    cogsAccount: '',
    assetAccount: '',
    taxAgency: ''
  });
  const [errors, setErrors] = useState({});

  // Initialize form data when item changes
  useEffect(() => {
    if (item && mode === 'edit') {
      setFormData({
        itemNumber: item.itemNumber || '',
        description: item.description || '',
        purchaseDescription: item.purchaseDescription || '',
        isActive: item.isActive !== undefined ? item.isActive : true,
        quantityOnHand: item.quantityOnHand || 0,
        cost: item.cost || 0,
        price: item.price || 0,
        reorderPoint: item.reorderPoint || '',
        preferredVendor: item.preferredVendor || '',
        manufacturerPartNumber: item.manufacturerPartNumber || '',
        salesTaxCode: item.salesTaxCode || '',
        account: item.account || '',
        cogsAccount: item.cogsAccount || '',
        assetAccount: item.assetAccount || '',
        taxAgency: item.taxAgency || ''
      });
    } else {
      // Reset form for create mode
      setFormData({
        itemNumber: '',
        description: '',
        purchaseDescription: '',
        isActive: true,
        quantityOnHand: 0,
        cost: 0,
        price: 0,
        reorderPoint: '',
        preferredVendor: '',
        manufacturerPartNumber: '',
        salesTaxCode: '',
        account: '',
        cogsAccount: '',
        assetAccount: '',
        taxAgency: ''
      });
    }
    setErrors({});
  }, [item, mode, isOpen]);

  // Create mutation
  const createMutation = useMutation(
    (data) => inventoryAPI.createInventoryItem(data),
    {
      onSuccess: (response) => {
        toast.success('Inventory item created successfully!');
        queryClient.invalidateQueries('inventory');
        const createdItem = response?.data?.item;
        if (onCreated && createdItem) {
          onCreated(createdItem);
        }
        onClose();
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors) {
          const fieldErrors = {};
          errorData.errors.forEach(err => {
            if (err.path) {
              fieldErrors[err.path] = err.msg;
            }
          });
          setErrors(fieldErrors);
          toast.error(errorData.message || 'Please fix the highlighted fields');
        } else {
          toast.error(errorData?.message || 'Failed to create inventory item');
        }
      }
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    (data) => inventoryAPI.updateInventoryItem(item.id, data),
    {
      onSuccess: () => {
        toast.success('Inventory item updated successfully!');
        queryClient.invalidateQueries('inventory');
        queryClient.invalidateQueries(['inventory', item.id]);
        onClose();
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors) {
          const fieldErrors = {};
          errorData.errors.forEach(err => {
            if (err.path) {
              fieldErrors[err.path] = err.msg;
            }
          });
          setErrors(fieldErrors);
          toast.error('Please fix the highlighted fields');
        } else {
          toast.error(errorData?.message || 'Failed to update inventory item');
        }
      }
    }
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prepare data
    const submitData = {
      ...formData,
      quantityOnHand: parseFloat(formData.quantityOnHand) || 0,
      cost: parseFloat(formData.cost) || 0,
      price: parseFloat(formData.price) || 0,
      reorderPoint: formData.reorderPoint ? parseFloat(formData.reorderPoint) : null,
      purchaseDescription: formData.purchaseDescription || formData.description,
      preferredVendor: formData.preferredVendor || null,
      manufacturerPartNumber: formData.manufacturerPartNumber || null,
      salesTaxCode: formData.salesTaxCode || null,
      account: formData.account || null,
      cogsAccount: formData.cogsAccount || null,
      assetAccount: formData.assetAccount || null,
      taxAgency: formData.taxAgency || null
    };

    if (mode === 'create') {
      createMutation.mutate(submitData);
    } else {
      updateMutation.mutate(submitData);
    }
  };

  if (!isOpen) return null;

  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Package className="h-6 w-6 text-primary-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                {mode === 'create' ? 'Create Inventory Item' : 'Edit Inventory Item'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Item Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Item Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="itemNumber"
                  value={formData.itemNumber}
                  onChange={handleChange}
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.itemNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.itemNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.itemNumber}</p>
                )}
              </div>

              {/* Status */}
              <div className="flex items-center pt-7">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
              </div>

              {/* Purchase Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purchase Description
                </label>
                <input
                  type="text"
                  name="purchaseDescription"
                  value={formData.purchaseDescription}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Quantity On Hand */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity On Hand
                </label>
                <input
                  type="number"
                  name="quantityOnHand"
                  value={formData.quantityOnHand}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Cost */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cost
                </label>
                <input
                  type="number"
                  name="cost"
                  value={formData.cost}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Reorder Point */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reorder Point
                </label>
                <input
                  type="number"
                  name="reorderPoint"
                  value={formData.reorderPoint}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Manufacturer Part Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manufacturer Part Number (MPN)
                </label>
                <input
                  type="text"
                  name="manufacturerPartNumber"
                  value={formData.manufacturerPartNumber}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Preferred Vendor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Vendor
                </label>
                <input
                  type="text"
                  name="preferredVendor"
                  value={formData.preferredVendor}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Sales Tax Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sales Tax Code
                </label>
                <input
                  type="text"
                  name="salesTaxCode"
                  value={formData.salesTaxCode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Account */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account
                </label>
                <input
                  type="text"
                  name="account"
                  value={formData.account}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* COGS Account */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  COGS Account
                </label>
                <input
                  type="text"
                  name="cogsAccount"
                  value={formData.cogsAccount}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Asset Account */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asset Account
                </label>
                <input
                  type="text"
                  name="assetAccount"
                  value={formData.assetAccount}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Tax Agency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tax Agency
                </label>
                <input
                  type="text"
                  name="taxAgency"
                  value={formData.taxAgency}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
